import type { BookingGarment, ChangeGarmentStateInput } from '../api/bookings';

import {
  addAlteration,
  alterationProblems,
  fromDetails,
  tailorProblems,
  toInput,
  type DraftAlteration,
} from './alterations';
import { movesFor } from './garmentMoves';

/**
 * Sending one garment on a booking to the tailor — the website's garment panel and the phone's
 * "Send to the tailor" screen, one request (spec §11).
 *
 * <p>
 * <strong>The job number is never sent.</strong> The server issues it and stamps it on the line;
 * a non-null number is how a shop with cards already written keeps quoting them, so echoing back the
 * one the server issued would take that escape hatch away.
 * </p>
 *
 * <p>
 * <strong>The measurements are always sent on this move, never omitted</strong>: the list is a
 * statement, so an empty one is how a clerk says nothing is outstanding.
 * </p>
 */

export interface TailorDraft {
  /** The tailor's name — always filled when someone is chosen, even one on neither list. */
  tailorName: string;
  /** At most one of these two: which master the tailor is on, when they are on one. */
  vendorId: number | null;
  employeeId: number | null;
  dueOn: string;
  /** What the tailor is paid, as typed. Recorded whether or not the customer is billed. */
  charge: string;
  billable: boolean;
  /** A fitting alteration, undone when the garment comes back — against a permanent repair. */
  reversible: boolean;
  work: DraftAlteration[];
  reason: string;
}

/** Starts from what the garment already carries, with a blank row so there is somewhere to type. */
export function tailorDraft(garment: BookingGarment): TailorDraft {
  return {
    tailorName: garment.alterationTailorName ?? '',
    vendorId: garment.alterationTailorVendorId,
    employeeId: garment.alterationTailorEmployeeId,
    dueOn: garment.alterationDueOn ?? '',
    charge: garment.alterationCharge === null ? '' : String(garment.alterationCharge),
    billable: garment.alterationBillable,
    reversible: garment.alterationIsReversible,
    work: addAlteration(fromDetails(garment.alterations)),
    reason: '',
  };
}

/** Why this would be refused, in the order a person fixes them. Empty means it would go. */
export function tailorMoveProblems(draft: TailorDraft, today: string): string[] {
  const problems = [...tailorProblems(draft.dueOn, today), ...alterationProblems(draft.work)];

  if (draft.charge.trim() !== '') {
    const charge = Number(draft.charge);

    if (!Number.isFinite(charge) || charge < 0) {
      problems.push('The cost of the work has to be a figure, nothing or more.');
    }
  }

  return problems;
}

/** The alteration half of the move — what both clients add to `{ state: 'Alteration' }`. */
export function alterationFields(draft: TailorDraft): Omit<ChangeGarmentStateInput, 'state'> {
  return {
    alterationJobNumber: null,
    alterationTailorName: draft.tailorName.trim() || null,
    alterationTailorVendorId: draft.vendorId,
    alterationTailorEmployeeId: draft.employeeId,
    alterationDueOn: draft.dueOn || null,
    alterationCharge: draft.charge.trim() === '' ? null : Number(draft.charge),
    alterationBillable: draft.billable,
    alterationIsReversible: draft.reversible,
    alterations: toInput(draft.work),
  };
}

/** The whole request: to the tailor, or — for one already there — the docket corrected. */
export function tailorMoveRequest(draft: TailorDraft): ChangeGarmentStateInput {
  return {
    state: 'Alteration',
    reason: draft.reason.trim() || null,
    ...alterationFields(draft),
  };
}

/**
 * What the garment's tailor button says, or null when it has none: a garment that can go to the
 * tailor is sent, one already there has its docket corrected (the server takes that same-state
 * move only with measurements, which this always sends).
 */
export function tailorAction(garment: BookingGarment): 'send' | 'update' | null {
  if (garment.state === 'Alteration') {
    return 'update';
  }

  return movesFor(garment.state).includes('Alteration') ? 'send' : null;
}

/**
 * What saving does with the cloth, said under the tailor (docs/Garment_Job_Simplification_Design.md
 * §4): **sending to an outside tailor is the dispatch**, so the challan is issued as the docket is
 * saved; one of the shop's own keeps it in the shop; a name on neither list waits in the
 * Alterations queue to be sent out from there. Null until somebody is chosen.
 */
export function handOverWords(
  draft: Pick<TailorDraft, 'tailorName' | 'vendorId' | 'employeeId'>,
): string | null {
  const name = draft.tailorName.trim();

  if (draft.vendorId !== null) {
    return `Saving sends it out to ${name || 'the tailor'} on a challan — nothing more to do in the queue.`;
  }

  if (draft.employeeId !== null) {
    return `It stays in the shop with ${name || 'your tailor'} — no challan.`;
  }

  return name === ''
    ? null
    : `${name} is not on your lists, so it waits in the Alterations queue to be sent out.`;
}
